import React, { useCallback, useEffect, useState } from 'react';
import { FaCopy, FaExchangeAlt, FaFileExcel, FaPlus, FaTable, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import CabecalhoEventoAereo from '../../components/CabecalhoEventoAereo';
import ConfirmarAcaoModal from '../../components/ConfirmarAcaoModal';
import { FinCheck, FinChecks } from '../../components/FinField';
import ErpTemplate from '../../components/ErpTemplate';
import api from '../../services/api';
import notify from '../../services/notify';
import { ROTULO_SITUACAO, COR_SITUACAO, cuidaDoPedido, PERFIS_AEREO } from '../../util/malhaAereo';
import { useAtendimentoAereo } from '../../components/AtendimentoAereo';
import '../../css/financeiro-parcela-pagar.css';

/**
 * Painel de Malhas de UMA solicitação — o nível de baixo do painel de Solicitações.
 *
 * Cada malha é uma rodada de cotação recebida para aquele pedido, e o Aéreo abre
 * rodada nova por aqui. A Importação é endereçada pelo id da malha, então a rodada
 * precisa existir antes do upload — e é este painel que a cria. Ele aparece sempre,
 * mesmo com uma malha só: tela que muda de forma conforme a quantidade de dado
 * obriga o usuário a aprender dois caminhos.
 */
export default function CadastroEventoMalhasAereo({ match }) {
    const idSolicitacao = match.params.idSolicitacao;
    const history = useHistory();

    const token = localStorage.getItem('token');

    const [malhas, setMalhas] = useState([]);
    const [headerEvento, setHeaderEvento] = useState({});
    const [idEvento, setIdEvento] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [criando, setCriando] = useState(false);
    // Por que "Nova malha" está preso agora (ex.: solicitação ainda não enviada ao Aéreo).
    // Vem do back, que é quem recusa.
    const [motivoNaoAbrirMalha, setMotivoNaoAbrirMalha] = useState(null);
    const [situacaoSolicitacao, setSituacaoSolicitacao] = useState(null);
    const [duplicando, setDuplicando] = useState(null); // id da malha sendo copiada
    // Duplicar com destino (mais de uma malha): { m, numero } da origem, e o destino escolhido —
    // 'nova' (rodada nova) ou o id de outra malha da solicitação.
    const [duplicacao, setDuplicacao] = useState(null);
    const [destinoDuplicar, setDestinoDuplicar] = useState('nova');
    // Levar para outra malha também o agrupamento desta: as edições de trecho e os trechos avulsos, que
    // sobrescrevem os iguais do destino. Desmarcado, vão só as cotações.
    const [comAgrupamento, setComAgrupamento] = useState(false);
    const [copiando, setCopiando] = useState(false);
    // Exclusão apaga cotações e uploads sem volta: confirma antes. { m, numero }
    const [exclusao, setExclusao] = useState(null);
    const [excluindo, setExcluindo] = useState(false);

    const perfil = localStorage.getItem('perfil');
    // Importar as opções é do RSVP — mesmo grupo de perfis do back (EventoAereoPortacaoService).
    const podeImportar = cuidaDoPedido(perfil);

    const carregar = useCallback(() => {
        setCarregando(true);
        api({ method: 'get', action: '/eventoMalhaAerea', token, params: { solicitacao: idSolicitacao } })
            .then(res => {
                const d = res.data.data ?? {};
                setMalhas(d.malhas ?? []);
                setIdEvento(d.evento ?? null);
                setMotivoNaoAbrirMalha(d.motivoNaoAbrirMalha ?? null);
                setSituacaoSolicitacao(d.situacaoSolicitacao ?? null);
            })
            .catch(err => notify.apiError(err, 'Erro ao carregar as malhas'))
            .finally(() => setCarregando(false));
    }, [idSolicitacao, token]);

    // Atendimento do Aéreo: sem atender o evento, o Aéreo só lê. Assumido, a tela relê as travas.
    const atendimento = useAtendimentoAereo(idEvento, carregar);
    // Abrir rodada nova é do Aéreo (o gerente incluso), que é quem recebe cotação. admin entra por bypass.
    // O que trava quem é do Aéreo — não atender o evento, pedido que não foi enviado — vem do back
    // (`motivoNaoAbrirMalha`): o botão aparece preso, com o motivo, e a tela sem malha o repete.
    const podeAbrirMalha = PERFIS_AEREO.includes(perfil) || perfil === 'admin';

    useEffect(() => {
        carregar();
        api({ method: 'get', action: '/eventoAereoCabecalho', token, params: { solicitacao: idSolicitacao } })
            .then(res => setHeaderEvento(res.data.data ?? {}))
            .catch(() => setHeaderEvento({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [carregar, idSolicitacao]);

    // Cada malha é endereçada pelo id DELA: é o que permite a segunda rodada existir
    // sem o Salvar de uma apagar as cotações da outra.
    const abrirMalha = (m) => history.push('/cadastroEventoImportarMalhaAereo/' + m.id);

    const criar = () => {
        setCriando(true);
        api({ method: 'post', action: '/eventoMalhaAerea', token, data: { solicitacao: idSolicitacao } })
            .then(res => {
                toast.success('Malha aberta');
                // A malha nova nasce vazia e o próximo passo é importar a cotação: abre direto.
                const id = res.data?.data?.id;
                if (id) {
                    history.push('/cadastroEventoImportarMalhaAereo/' + id);
                    return;
                }
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao abrir a malha'))
            .finally(() => setCriando(false));
    };

    // Rodada nova a partir desta: cabeçalho, grupos e cotações. Sem confirmação — a cópia
    // nasce rascunho, e rascunho criado por engano se exclui.
    const duplicar = (m, numero) => {
        setDuplicando(m.id);
        api({ method: 'post', action: '/eventoMalhaAerea/' + m.id + '/duplicar', token })
            .then(() => {
                toast.success(`Malha ${numero} duplicada — a cópia entrou como rascunho`);
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao duplicar a malha'))
            .finally(() => setDuplicando(null));
    };

    // Outra malha da solicitação que pode receber as cotações desta (o back diz por linha).
    const destinosLivres = (m) => malhas.filter(x => x.id !== m.id && !x.motivoNaoReceber);
    // Com uma malha só, o Duplicar abre a rodada nova direto, como sempre. Com mais de uma, pergunta
    // o destino: rodada nova ou outra malha, para juntar as duas rodadas numa só.
    const pedirDuplicar = (m, numero) => {
        if (malhas.length < 2) {
            duplicar(m, numero);
            return;
        }
        const livre = destinosLivres(m)[0];
        setDestinoDuplicar(!m.motivoNaoDuplicar ? 'nova' : (livre ? livre.id : ''));
        setComAgrupamento(false);
        setDuplicacao({ m, numero });
    };
    const copiarPara = (m, numero, idDestino) => {
        const numeroDestino = malhas.findIndex(x => x.id === idDestino) + 1;
        setCopiando(true);
        api({ method: 'post', action: '/eventoMalhaAerea/' + m.id + '/copiar', token, data: { destino: idDestino, agrupamento: comAgrupamento } })
            .then(res => {
                const { copiadas = 0, puladas = 0, grupos = 0, regrupadas = 0, vinculadas = 0, unidos = 0 } = res.data.data ?? {};
                toast.success(`${copiadas} ${copiadas !== 1 ? 'cotações' : 'cotação'} da Malha ${numero} na Malha ${numeroDestino}`
                    + (puladas ? ` · ${puladas} já ${puladas !== 1 ? 'estavam' : 'estava'} lá` : '')
                    + (grupos ? ` · ${grupos} ${grupos !== 1 ? 'trechos do agrupamento' : 'trecho do agrupamento'}` : '')
                    + (regrupadas ? ` · ${regrupadas} ${regrupadas !== 1 ? 'mudaram' : 'mudou'} para o agrupamento desta` : '')
                    + (vinculadas ? ` · ${vinculadas} ${vinculadas !== 1 ? 'relacionadas' : 'relacionada'} automaticamente` : '')
                    + (unidos ? ` · ${unidos} ${unidos !== 1 ? 'grupos iguais unidos' : 'grupo igual unido'}, com as opções` : ''));
                setDuplicacao(null);
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao copiar as cotações'))
            .finally(() => setCopiando(false));
    };
    const confirmarDuplicacao = () => {
        const { m, numero } = duplicacao;
        if (destinoDuplicar === 'nova') {
            setDuplicacao(null);
            duplicar(m, numero);
            return;
        }
        copiarPara(m, numero, destinoDuplicar);
    };

    const excluir = () => {
        setExcluindo(true);
        api({ method: 'delete', action: '/eventoMalhaAerea/' + exclusao.m.id, token })
            .then(() => {
                toast.success('Malha excluída');
                setExclusao(null);
                carregar();
            })
            .catch(err => notify.apiError(err, 'Erro ao excluir a malha'))
            .finally(() => setExcluindo(false));
    };

    const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;

    const dataCurta = (iso) => {
        if (!iso) return '—';
        const d = new Date(String(iso).replace(' ', 'T'));
        return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
    };
    // "dd/mm/aaaa hh:mm" da última edição (`ultimaEdicao.em`, horário do servidor, sem fuso).
    const dataHoraCurta = (v) => {
        const m = String(v ?? '').match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
        return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : '';
    };

    // Rótulo e cor da situação. A LISTA de valores é do back
    // (`EventoMalhaAerea::situacoes()`); aqui mora só o texto e a cor, que são da tela
    // — mesmo arranjo que o painel de auditoria usa para os módulos.
    //
    // Valor desconhecido NÃO vira "Rascunho": aparece cru e apagado. A conexão roda
    // com `strict => false`, então um valor fora da lista chega até aqui em vez de ser
    // recusado no banco — e o pior desfecho seria a tela exibi-lo como se fosse um
    // estado válido.
    const pillSituacao = (m) => {
        const v = String(m.situacao ?? '').trim();
        if (v === '') return <span className="fin-pp__pill is-muted">—</span>;
        const rotulo = ROTULO_SITUACAO[v];
        if (!rotulo) return <span className="fin-pp__pill is-muted" title="Situação desconhecida">{v}</span>;
        return (
            <span className="d-inline-flex align-items-center gap-1 flex-wrap">
                <span className={'fin-pp__pill ' + COR_SITUACAO[v]}>{rotulo}</span>
                {/* Mexida depois do envio ou da aprovação, sem passar pela alteração formal.
                    Some na próxima decisão. Só o grupo; a pessoa fica na auditoria. */}
                {m.alteradoPor && (
                    <span className="fin-pp__pill is-warn" title="Cotação mudou depois do envio">
                        Alterado pelo {m.alteradoPor}
                    </span>
                )}
            </span>
        );
    };

    const totalOpcoes = malhas.reduce((a, m) => a + (Number(m.opcoes) || 0), 0);

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">

                <CabecalhoEventoAereo
                    titulo="Malhas da Solicitação"
                    nomeEvento={headerEvento.nomeEvento}
                    cliente={headerEvento.cliente}
                    solicitante={headerEvento.solicitante}
                    dataEvento={headerEvento.dataEvento}
                    localEvento={headerEvento.localEvento}
                    tktMedio={headerEvento.tktMedio ?? null}
                    voosSolicitados={`${totalOpcoes} ${totalOpcoes !== 1 ? 'cotações' : 'cotação'}`}
                    atalhos={[
                        atendimento.atalho,
                        idEvento && {
                            rotulo: 'Solicitações',
                            titulo: 'Volta ao painel de Solicitações deste evento.',
                            to: '/cadastroEventoSolicitacoesAereo/' + idEvento,
                        },
                        {
                            rotulo: 'Solicitação',
                            titulo: 'Origens, destino, datas e trechos desta solicitação.',
                            to: '/cadastroEventoSolicitacaoAereo/' + idSolicitacao,
                        },
                        // Só para quem abre a Importação das Opções: o RSVP (e admin), o mesmo grupo
                        // que o back aceita. É navegação, então aparece com ou sem malha aprovada.
                        podeImportar && idEvento && {
                            rotulo: 'Importação das Opções',
                            titulo: 'Importa as opções das malhas aprovadas para o participante escolher.',
                            to: '/cadastroEventoPortacaoAereo/' + idEvento,
                        },
                    ]}
                />

                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Malhas</h1>
                        <p className="fin-pp__subtitle">
                            Cada malha é uma rodada de cotação recebida para esta solicitação.
                        </p>
                    </div>
                    {(podeAbrirMalha || podeImportar) && (
                        <div className="fin-pp__actions">
                            {/* Só com malha aprovada (ou liberada, no nacional) e solicitação não
                                cancelada: é o que a importação usa (cancelar encerra as malhas do pedido). */}
                            {podeImportar && idEvento && situacaoSolicitacao !== 'cancelada'
                                && malhas.some(m => ['aprovada', 'liberada', 'importada'].includes(m.situacao)) && (
                                <button type="button" className="fin-pp__btn fin-pp__btn--pill"
                                    title="Importa as opções das malhas aprovadas ou liberadas do evento para as opções que o participante escolhe"
                                    onClick={() => history.push('/cadastroEventoPortacaoAereo/' + idEvento)}>
                                    <FaExchangeAlt size={12} style={{ marginRight: 6 }} />
                                    Importar opções de aéreo
                                </button>
                            )}
                            {podeAbrirMalha && (
                                <button type="button" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--primary"
                                    disabled={criando || !!motivoNaoAbrirMalha}
                                    title={motivoNaoAbrirMalha || 'Abre uma rodada nova de cotação nesta solicitação'}
                                    onClick={criar}>
                                    <FaPlus size={12} style={{ marginRight: 6 }} />
                                    {criando ? 'Abrindo...' : 'Nova malha'}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 760, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th>Malha</th>
                                    <th>Recebida em</th>
                                    <th>Última edição</th>
                                    <th>Agência</th>
                                    <th>Situação</th>
                                    <th style={{ textAlign: 'center' }}>Cotações</th>
                                    <th style={{ textAlign: 'center' }}>Uploads</th>
                                    <th style={{ width: 1 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {carregando && (
                                    <tr><td colSpan={8} className="fin-pp__empty">Carregando...</td></tr>
                                )}
                                {/* Sem malha: diz por que o "Nova malha" está preso, ou que só o
                                    Aéreo abre — o botão é só do cabeçalho, e o motivo no title dele
                                    não aparece para quem não passa o mouse. */}
                                {!carregando && malhas.length === 0 && (
                                    <tr><td colSpan={8} className="fin-pp__empty">
                                        Nenhuma malha nesta solicitação.
                                        {!podeAbrirMalha
                                            ? ' Só o Aéreo abre uma malha nova.'
                                            : (motivoNaoAbrirMalha ? ' ' + motivoNaoAbrirMalha : '')}
                                    </td></tr>
                                )}
                                {!carregando && malhas.map((m, i) => (
                                    <tr key={m.id}>
                                        <td className="fin-pp__cell-strong">
                                            <span className="fin-pp__cphead-ic" style={{ marginRight: 8 }}>
                                                <FaTable size={13} />
                                            </span>
                                            Malha {i + 1}
                                        </td>
                                        <td className="fin-pp__cell-muted">{dataCurta(m.created_at)}</td>
                                        {/* Quem fez a última gravação de conteúdo da malha (cabeçalho, grupos,
                                            opções, uploads) e quando. Envio e aprovação não contam. */}
                                        <td className="fin-pp__cell-muted" style={{ whiteSpace: 'nowrap' }}>
                                            {m.ultimaEdicao ? (
                                                <>
                                                    <div style={{ color: 'var(--fin-tx)' }}>{m.ultimaEdicao.nome}</div>
                                                    <div style={{ fontSize: 12 }}>{dataHoraCurta(m.ultimaEdicao.em)}</div>
                                                </>
                                            ) : '—'}
                                        </td>
                                        <td className="fin-pp__cell-muted">{m.agenciaIntermediaria || '—'}</td>
                                        <td>{pillSituacao(m)}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={'fin-pp__pill ' + (Number(m.opcoes) ? 'is-info' : 'is-muted')}>
                                                {Number(m.opcoes) || 0}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={'fin-pp__pill ' + (Number(m.arquivos) ? 'is-ok' : 'is-muted')}>
                                                {Number(m.arquivos) || 0}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-2 justify-content-end">
                                                {/* O rascunho do Aéreo aparece na lista para todos,
                                                    mas só abre para o Aéreo — o motivo vem do back. */}
                                                <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                    disabled={!!m.motivoNaoAbrir}
                                                    title={m.motivoNaoAbrir || undefined}
                                                    onClick={() => abrirMalha(m)}>
                                                    <FaFileExcel size={12} style={{ marginRight: 6 }} />
                                                    Abrir
                                                </button>
                                                {/* Duplicar e excluir só aparecem quando podem: o motivo
                                                    de cada um vem do back, por linha. O "Nova malha" do
                                                    cabeçalho já explica a trava de abrir rodada. */}
                                                {(!m.motivoNaoDuplicar || destinosLivres(m).length > 0) && (
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                        disabled={duplicando === m.id}
                                                        title="Abre uma malha nova com o cabeçalho, os grupos e as cotações desta, ou leva as cotações para outra malha da solicitação. Uploads e aprovações não vão junto."
                                                        onClick={() => pedirDuplicar(m, i + 1)}>
                                                        <FaCopy size={11} style={{ marginRight: 6 }} />
                                                        {duplicando === m.id ? 'Duplicando...' : 'Duplicar'}
                                                    </button>
                                                )}
                                                {!m.motivoNaoExcluir && (
                                                    <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                        title="Excluir a malha (só enquanto é rascunho)"
                                                        onClick={() => setExclusao({ m, numero: i + 1 })}>
                                                        <FaTrash size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">
                            {malhas.length} {malhas.length !== 1 ? 'malhas' : 'malha'}
                        </div>
                    </div>
                </div>

                {exclusao && (
                    <ConfirmarAcaoModal
                        aberto
                        titulo={`Excluir a Malha ${exclusao.numero}?`}
                        subtitulo={`Apaga ${plural(Number(exclusao.m.opcoes) || 0, 'cotação', 'cotações')}, os grupos e ${plural(Number(exclusao.m.arquivos) || 0, 'upload', 'uploads')} deste rascunho. A tela não desfaz.`}
                        rotulo="Excluir malha"
                        tom="danger"
                        icone="danger"
                        processando={excluindo}
                        onConfirmar={excluir}
                        onFechar={() => setExclusao(null)}
                    />
                )}
            </div>
            {duplicacao && (
                <ConfirmarAcaoModal
                    aberto
                    titulo={`Duplicar a Malha ${duplicacao.numero}`}
                    subtitulo="Escolha para onde vão as cotações desta malha. Ela continua como está."
                    rotulo={destinoDuplicar === 'nova' ? 'Duplicar' : 'Levar as cotações'}
                    tom="primary"
                    icone="enviar"
                    processando={copiando || duplicando === duplicacao.m.id}
                    confirmarPreso={!destinoDuplicar}
                    onConfirmar={confirmarDuplicacao}
                    onFechar={() => setDuplicacao(null)}
                >
                    <FinChecks>
                        <FinCheck type="radio" id="destino-nova" name="destinoDuplicar"
                            label="Malha nova, em rascunho, com o cabeçalho, os grupos e as cotações desta"
                            title={duplicacao.m.motivoNaoDuplicar || undefined}
                            checked={destinoDuplicar === 'nova'}
                            disabled={!!duplicacao.m.motivoNaoDuplicar}
                            onChange={() => setDestinoDuplicar('nova')} />
                        {malhas.map((x, j) => x.id !== duplicacao.m.id && (
                            <FinCheck key={x.id} type="radio" id={'destino-' + x.id} name="destinoDuplicar"
                                label={`Malha ${j + 1} · ${ROTULO_SITUACAO[x.situacao] ?? x.situacao}`}
                                title={x.motivoNaoReceber || undefined}
                                checked={destinoDuplicar === x.id}
                                disabled={!!x.motivoNaoReceber}
                                onChange={() => setDestinoDuplicar(x.id)} />
                        ))}
                    </FinChecks>
                    {/* Só numa malha que já existe: a rodada nova leva tudo. Marcar é o aceite de que o
                        agrupamento desta prevalece: as edições sobrescrevem as iguais do destino, e as
                        cotações que já estão lá passam a seguir o vínculo desta. */}
                    {destinoDuplicar && destinoDuplicar !== 'nova' && (
                        <div style={{ marginTop: 12 }}>
                            <FinCheck id="copiar-agrupamento" checked={comAgrupamento}
                                label="Copiar também o agrupamento: as edições de trecho e os trechos avulsos desta malha. As cotações que já estão lá passam a seguir o agrupamento desta."
                                onChange={e => setComAgrupamento(e.target.checked)} />
                        </div>
                    )}
                    <p className="fin-pp__cell-muted" style={{ margin: '12px 0 0' }}>
                        Numa malha que já existe, entram só as cotações que ela ainda não tem (mesmo voo, cia e data em cada perna); as que já estão lá ficam com os valores delas. Sem copiar o agrupamento, vale o da malha de destino, e a cotação nova sem vínculo é relacionada automaticamente. Trecho avulso que fica igual a outro trecho no destino se une a ele, com as cotações.
                    </p>
                </ConfirmarAcaoModal>
            )}
            {atendimento.modal}
        </ErpTemplate>
    );
}
